import { apolloClient } from '@/apolloClient';
import { gql } from '@apollo/client/core';
import { useBranding } from '@/composables/useBranding';

const REGISTER_PUSH_SUBSCRIPTION = gql`
  mutation RegisterPushSubscription($input: RegisterPushSubscriptionInput!) {
    registerPushSubscription(input: $input)
  }
`;

const UNREGISTER_PUSH_SUBSCRIPTION = gql`
  mutation UnregisterPushSubscription($endpoint: String!) {
    unregisterPushSubscription(endpoint: $endpoint)
  }
`;

let swRegistration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service workers are not supported');
    return null;
  }

  try {
    swRegistration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    return swRegistration;
  } catch (err) {
    console.error('SW registration failed:', err);
    return null;
  }
}

export function getServiceWorkerRegistration(): ServiceWorkerRegistration | null {
  return swRegistration;
}

export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    return 'denied';
  }

  if (Notification.permission === 'granted') {
    return 'granted';
  }

  return await Notification.requestPermission();
}

export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscription | null> {
  const registration = swRegistration || (await registerServiceWorker());
  if (!registration) return null;

  const permission = await requestNotificationPermission();
  if (permission !== 'granted') return null;

  try {
    const existing = await registration.pushManager.getSubscription();
    if (existing) return existing;

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as BufferSource,
    });

    return subscription;
  } catch (err) {
    console.error('Push subscription failed:', err);
    return null;
  }
}

export async function sendSubscriptionToServer(subscription: PushSubscription): Promise<void> {
  const key = subscription.getKey('p256dh');
  const auth = subscription.getKey('auth');

  await apolloClient.mutate({
    mutation: REGISTER_PUSH_SUBSCRIPTION,
    variables: {
      input: {
        endpoint: subscription.endpoint,
        public_key: key ? btoa(String.fromCharCode(...new Uint8Array(key))) : null,
        auth_token: auth ? btoa(String.fromCharCode(...new Uint8Array(auth))) : null,
        content_encoding: (PushManager.supportedContentEncodings || ['aesgcm'])[0],
      },
    },
  });
}

export async function unsubscribeFromPush(): Promise<void> {
  const registration = swRegistration || (await navigator.serviceWorker?.getRegistration());
  if (!registration) return;

  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) return;

  const endpoint = subscription.endpoint;
  await subscription.unsubscribe();

  try {
    await apolloClient.mutate({
      mutation: UNREGISTER_PUSH_SUBSCRIPTION,
      variables: { endpoint },
    });
  } catch {
    // Server cleanup is best-effort
  }
}

export function showLocalNotification(title: string, body: string, data?: Record<string, unknown>): void {
  if (!swRegistration?.active) return;
  if (Notification.permission !== 'granted') return;

  // Brand the notification with the tenant's favicon when set (the service
  // worker falls back to the platform icon otherwise).
  const { branding } = useBranding();
  const icon = branding.value?.favicon_url || undefined;

  swRegistration.active.postMessage({
    type: 'SHOW_NOTIFICATION',
    title,
    body,
    tag: data?.channel_id ? `chat-channel-${data.channel_id}` : 'chat-message',
    data,
    icon,
    badge: icon,
  });
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}
